import type { Editor, Range, SuggestionListRef } from 'OpalChat/TiptapEditorTypes';
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { Box, Flex, Separator, Text } from '@optiaxiom/react';
import {
  IconBracketsCurly,
  IconCalendar,
  IconFile,
  IconInputNumeric,
  IconInputText,
  IconPen,
  IconPlus,
  IconToggleOff,
} from '@optimizely/axiom-icons';

import type { AgentParameter, AgentParameterType } from '@frontend/types';

import type { SuggestionItem } from '../types';

import { useSuggestionKeyboard } from './hooks/useSuggestionKeyboard';
import { styles } from './AgentFormPromptTemplate.css';
import { SectionHeader, SuggestionItem as SuggestionItemComponent } from './Common';

const VARIABLE_TYPE_ICONS: Record<string, React.ReactNode> = {
  string: <IconInputText />,
  number: <IconInputNumeric />,
  boolean: <IconToggleOff />,
  datetime: <IconCalendar />,
  json: <IconBracketsCurly />,
  object: <IconBracketsCurly />,
  file: <IconFile />,
};

export type VariableSuggestionListProps = {
  editor: Editor;
  range: Range;
  command: (item: SuggestionItem) => void;
  query: string;
  variables: Array<AgentParameter<AgentParameterType>> | null;
  onCreateVariable: () => void;
  onEditVariable: (variable: AgentParameter<AgentParameterType>) => void;
  onInsertVariable?: (editor: Editor, variableName: string) => void;
};

export const VariableSuggestionList = forwardRef<SuggestionListRef, VariableSuggestionListProps>(
  (
    {
      editor,
      range,
      command,
      query,
      variables,
      onCreateVariable,
      onEditVariable,
      onInsertVariable,
    },
    ref
  ) => {
    const lowerQuery = query?.toLowerCase() ?? '';

    const filteredVariables = useMemo(
      () => variables?.filter(v => v.name.toLowerCase().includes(lowerQuery)) ?? [],
      [variables, lowerQuery]
    );

    const showCreateAction = !lowerQuery || 'create variable'.includes(lowerQuery);

    const suggestionItems: SuggestionItem[] = useMemo(
      () => [
        ...filteredVariables.map(v => ({
          id: `variable:${v.name}`,
          label: v.name,
          type: 'variable' as const,
          data: v,
        })),
        ...(showCreateAction
          ? [
              {
                id: 'action:create-variable',
                label: 'Create variable',
                type: 'action' as const,
                actionType: 'create-variable' as const,
              },
            ]
          : []),
      ],
      [filteredVariables, showCreateAction]
    );

    const handleSelect = useCallback(
      (item: SuggestionItem) => {
        editor.chain().focus().deleteRange(range).run();
        if (item.type === 'variable' && item.data && onInsertVariable) {
          onInsertVariable(editor, (item.data as AgentParameter<AgentParameterType>).name);
        } else if (item.actionType === 'create-variable') {
          onCreateVariable();
        }
        command(item);
      },
      [editor, range, command, onInsertVariable, onCreateVariable]
    );

    const handleEdit = useCallback(
      (item: SuggestionItem) => {
        if (item.type === 'variable' && item.data) {
          onEditVariable(item.data as AgentParameter<AgentParameterType>);
        }
      },
      [onEditVariable]
    );

    const { selectedId, isEditFocused, onKeyDown, handleItemClick } = useSuggestionKeyboard({
      items: suggestionItems,
      query,
      onSelect: handleSelect,
      onEdit: handleEdit,
      hasEditAction: true,
    });

    useImperativeHandle(ref, () => ({ onKeyDown }), [onKeyDown]);

    if (suggestionItems.length === 0) {
      return (
        <Box
          rounded="md"
          shadow="md"
          border="1"
          borderColor="border.default"
          bg="bg.default"
          p="6"
          className={styles.container}
          maxH="md"
        >
          <Text textAlign="center" color="fg.tertiary" fontSize="sm">
            No variables found
          </Text>
        </Box>
      );
    }

    return (
      <Flex
        flexDirection="column"
        gap="6"
        rounded="md"
        shadow="md"
        border="1"
        borderColor="border.default"
        overflow="auto"
        bg="bg.default"
        pt="6"
        pb="12"
        className={styles.container}
      >
        <SectionHeader title="Variables" />
        {filteredVariables.map(variable => {
          const itemId = `variable:${variable.name}`;
          const isSelected = selectedId === itemId;
          return (
            <SuggestionItemComponent
              key={variable.name}
              iconBefore={VARIABLE_TYPE_ICONS[variable.type] || <IconInputText />}
              label={variable.name}
              iconAfter={<IconPen />}
              isSelected={isSelected}
              isEditFocused={isSelected && isEditFocused}
              onClick={() => handleItemClick(itemId)}
              onEditClick={() => onEditVariable(variable)}
            />
          );
        })}
        {showCreateAction && (
          <>
            <Separator />
            <SuggestionItemComponent
              iconBefore={<IconPlus />}
              label="Create variable"
              isAction
              isSelected={selectedId === 'action:create-variable'}
              onClick={() => handleItemClick('action:create-variable')}
            />
          </>
        )}
      </Flex>
    );
  }
);

VariableSuggestionList.displayName = 'VariableSuggestionList';
